import { Stack, Typography } from '@mui/material'
import { useMemo } from 'react'

import { BatteryMeter } from 'src/components/VehicleInfo'
import {
  describeAge,
  freshnessColorPath,
  freshnessOf,
  type FreshnessState,
} from 'src/design-system'
import { useNow } from 'src/hooks/useNow'

import VehicleStatusRefreshIcon from './VehicleStatusRefresh'

// ----------------------------------------------------------------------
// What the car last said about itself, and when it said it.
//
// One line under the address on a vehicle card: the charge on the left, the
// age of the reading and the control that refetches it on the right. It
// replaces three objects that were none of the things they looked like.
//
//   the charge was drawn as a toggle   an orange stadium pill with a white
//                                      circle at one end, sixty pixels above a
//                                      real switch. One shape, two meanings,
//                                      on one card, and the one that cannot be
//                                      pressed was on top.
//   the age was drawn as a status chip the chip primitive means a domain and a
//                                      state, from a closed vocabulary. An age
//                                      is metadata about a reading and is
//                                      different a second later; at chip
//                                      weight it competed with the real
//                                      availability chip in the header.
//   the refresh was a bare glyph       24px, no hit target, and nothing saying
//                                      whether it refetched the battery, the
//                                      position or the card.
//
// **The reading is in the left column and the control is in the right one.**
// The left edge is the address line's above it, because charge and position
// are the same kind of fact. The right edge is the Freestyle toggle's below
// it, which is what gives the card a control column at all.
//
// **Only stale escalates in colour.** Fresh, recent and never-reported are all
// `text.secondary`; stale takes the attention ink, because it is the one case
// an operator has to act on. That rule is `freshnessColorPath(state, {
// stated: true })` rather than a local pair of colours, and the reason it is
// the `stated` form is argued where it lives.
//
// **The threshold is the telemetry policy's, not a number chosen here.** The
// design drew stale at an hour and said so: that was a guess, and the console
// already has an answer, in `freshness.policies.telemetry`.
//
// **Refreshing disables the control, not the row.** The bar and the figure
// keep the last known value at full strength while the read is out. Dimming
// them would say the reading is gone when it is merely old.
// ----------------------------------------------------------------------

/**
 * The sentence for an age, by what the age means.
 *
 * Two verbs and not one. `Refreshed` is a claim about this session: the car
 * answered while you were looking at it. `Last seen` is a claim about the car:
 * this is the most recent thing it said, and it was a while ago. Saying
 * `Refreshed 4h ago` about the second would be the console taking credit for a
 * reading it did not just take.
 *
 * The age itself is `describeAge`, the one formatter the product prints a
 * relative age with, so this row cannot drift from the rest of the console
 * about what four hours is called.
 */
export const telemetrySentence = (
  state: FreshnessState,
  ageMs: number | null,
): string => {
  if (state === 'unknown' || ageMs === null) {
    return 'Never reported'
  }
  if (state === 'unreachable') {
    return `Last seen ${describeAge(ageMs)}, source offline`
  }
  return state === 'fresh'
    ? `Refreshed ${describeAge(ageMs)}`
    : `Last seen ${describeAge(ageMs)}`
}

export interface TelemetryRowProps {
  /** The car, for the control that goes and reads it again. */
  vehicleId: number
  /** State of charge. `null` means the car has never reported one. */
  batteryLevel: number | null | undefined
  /** When the car last reported. `null` means never. */
  updatedAt: string | null | undefined
  /** Re-read whatever else the card is showing, after the car answers. */
  onRefreshed?: () => void
}

export default function TelemetryRow({
  vehicleId,
  batteryLevel,
  updatedAt,
  onRefreshed,
}: TelemetryRowProps) {
  // The age has to keep counting: a card left open for a shift must not go on
  // saying `just now` about a reading from an hour ago. One shared ticker.
  const now = useNow()
  const freshness = useMemo(
    () => freshnessOf({ readAt: updatedAt, policy: 'telemetry', now }),
    [updatedAt, now],
  )

  return (
    <Stack
      data-testid="telemetry-row"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ gap: 2 }}
    >
      <BatteryMeter level={batteryLevel} />

      <Stack direction="row" alignItems="center" sx={{ gap: 0.5 }}>
        {/* Plain text on no ground. It is metadata about the reading beside
            it, not a categorical status, so it must not take the chip. */}
        <Typography
          variant="body2"
          data-freshness={freshness.state}
          sx={{
            color: freshnessColorPath(freshness.state, { stated: true }),
            fontWeight: freshness.state === 'stale' ? 500 : 400,
            whiteSpace: 'nowrap',
          }}
        >
          {telemetrySentence(freshness.state, freshness.ageMs)}
        </Typography>

        <VehicleStatusRefreshIcon
          vehicleId={vehicleId}
          onRefreshed={onRefreshed}
        />
      </Stack>
    </Stack>
  )
}
